import type { IConditionBaseDropDownProps } from "@similarweb/pro-logic-components";

export const multiIncludeExcludeType = {
    KEYWORD: "keyword",
    URL: "url",
    TITLE: "title",
    TOPIC: "topic",
} as const;

export const MAX_KEYWORDS_PER_RULE = 20;
export const MAX_RULES = 3;
export const TEXT_BOX_WIDTH = 400;

const popupConfig = {
    width: 700,
    enabled: true,
    placement: "ontop-left",
    cssClassContent: "multiIncludeExclude--Popup-container keywords",
    cssClassContainer: "Popup-element-wrapper-triangle multiIncludeExclude--Popup-container keywords",
};

export const DROPDOWN_TYPE_OPTIONS: IConditionBaseDropDownProps = {
    options: [
        { id: "Include", title: "multi.include.exclude.keywords.include" },
        { id: "Exclude", title: "multi.include.exclude.keywords.exclude" },
    ],
    width: 108,
    buttonWidth: 110,
    ruleProperty: "type",
};
export const DROPDOWN_MATCH_TYPE_OPTIONS: Record<
    (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
    IConditionBaseDropDownProps
> = {
    [multiIncludeExcludeType.KEYWORD]: {
        options: [
            {
                id: "Phrase",
                title: "multi.include.exclude.keywords.phrase.match.title",
                description: "multi.include.exclude.keywords.phrase.match.description",
            },
            {
                id: "Exact",
                title: "multi.include.exclude.keywords.exact.match.title",
                description: "multi.include.exclude.keywords.exact.match.description",
            },
        ],
        width: 318,
        buttonWidth: 140,
        ruleProperty: "match",
    },
    [multiIncludeExcludeType.URL]: {
        options: [
            {
                id: "Phrase",
                title: "multi.include.exclude.keywords.phrase.match.title",
                description: "multi.include.exclude.url.phrase.match.description",
            },
            {
                id: "Exact",
                title: "multi.include.exclude.keywords.exact.match.title",
                description: "multi.include.exclude.url.exact.match.description",
            },
        ],
        width: 318,
        buttonWidth: 140,
        ruleProperty: "match",
    },
    [multiIncludeExcludeType.TITLE]: {
        options: [
            {
                id: "Phrase",
                title: "multi.include.exclude.keywords.phrase.match.title",
                description: "multi.include.exclude.keywords.phrase.match.description",
            },
            {
                id: "Exact",
                title: "multi.include.exclude.keywords.exact.match.title",
                description: "multi.include.exclude.keywords.exact.match.description",
            },
        ],
        width: 318,
        buttonWidth: 140,
        ruleProperty: "match",
    },
    [multiIncludeExcludeType.TOPIC]: {
        options: [
            {
                id: "Phrase",
                title: "multi.include.exclude.keywords.phrase.match.title",
                description: "multi.include.exclude.keywords.phrase.match.description",
            },
            {
                id: "Exact",
                title: "multi.include.exclude.keywords.exact.match.title",
                description: "multi.include.exclude.keywords.exact.match.description",
            },
        ],
        width: 318,
        buttonWidth: 140,
        ruleProperty: "match",
    },
};

const HEADER_TEXT_KEY_KEYWORD = "multi.include.exclude.keywords.header.title";
const HEADER_TEXT_KEY_URL = "multi.include.exclude.urls.header.title";
const HEADER_TEXT_KEY_TITLE = "multi.include.exclude.title.header.title";
const HEADER_TEXT_KEY_TOPIC = "multi.include.exclude.topic.header.title";
const EMPTY_STATE_BUTTON_KEYWORD = "multi.include.exclude.keywords.button.empty.state";
const EMPTY_STATE_BUTTON_URL = "multi.include.exclude.urls.button.empty.state";
const EMPTY_STATE_BUTTON_TITLE = "multi.include.exclude.title.button.empty.state";
const EMPTY_STATE_BUTTON_TOPIC = "multi.include.exclude.topic.button.empty.state";
const CHIP_BUTTON_TEXT_SINGLE_KEYWORDS = "multi.include.exclude.keywords.button.single.rule";
const CHIP_BUTTON_TEXT_SINGLE_URL = "multi.include.exclude.urls.button.single.rule";
const CHIP_BUTTON_TEXT_SINGLE_TITLE = "multi.include.exclude.title.button.single.rule";
const CHIP_BUTTON_TEXT_SINGLE_TOPIC = "multi.include.exclude.topic.button.single.rule";
const CHIP_BUTTON_TEXT_URL = "multi.include.exclude.urls.button.multiple.rules";
const CHIP_BUTTON_TEXT_TITLE = "multi.include.exclude.titles.button.multiple.rules";
const CHIP_BUTTON_TEXT_KEYWORD = "multi.include.exclude.keywords.button.multiple.rules";
const CHIP_BUTTON_TEXT_TOPIC = "multi.include.exclude.topic.button.multiple.rules";
export const CHIP_SEPARATOR_TEXT_KEY = "OR";
export const RULE_SEPARATOR_TEXT_KEY = "multi.include.exclude.keywords.rule.row.separator";
export const MAX_ITEMS_PER_RULE_BANNER_TEXT_KEY = "multi.include.exclude.keywords.max.keywords.in.rule.tip";
export const ADD_RULE_TEXT_KEY = "multi.include.exclude.keywords.add.rule";
const ADD_RULE_TOOLTIP_TEXT_KEY = "multi.include.exclude.keywords.max.rules.tip.tooltip";
export const MAX_RULES_BANNER_TEXT_KEY = "multi.include.exclude.keywords.max.rules.tip";
export const AUTOCOMPLETE_MAX_ITEMS_BANNER_TEXT_KEY = "multi.include.exclude.keywords.max.keywords.in.rule.tip.tooltip";
export const PRIMARY_BUTTON_TEXT_KEY = "multi.include.exclude.keywords.apply";
export const SECONDARY_BUTTON_TEXT_KEY = "multi.include.exclude.keywords.cancel";
export const TYPE_KEYWORD = "keyword";
export const TYPE_KEYWORD_LIST = "keywordList";

export const MAX_KEYWORD_LIST_ITEMS = 800;

export const translationKeysMap = {
    [multiIncludeExcludeType.KEYWORD]: {
        headerTextKey: HEADER_TEXT_KEY_KEYWORD,
        emptyStateBtnTextKey: EMPTY_STATE_BUTTON_KEYWORD,
        chipBtnTextSingleKey: CHIP_BUTTON_TEXT_SINGLE_KEYWORDS,
        chipBtnTextKey: CHIP_BUTTON_TEXT_KEYWORD,
    },
    [multiIncludeExcludeType.URL]: {
        headerTextKey: HEADER_TEXT_KEY_URL,
        emptyStateBtnTextKey: EMPTY_STATE_BUTTON_URL,
        chipBtnTextSingleKey: CHIP_BUTTON_TEXT_SINGLE_URL,
        chipBtnTextKey: CHIP_BUTTON_TEXT_URL,
    },
    [multiIncludeExcludeType.TITLE]: {
        headerTextKey: HEADER_TEXT_KEY_TITLE,
        emptyStateBtnTextKey: EMPTY_STATE_BUTTON_TITLE,
        chipBtnTextSingleKey: CHIP_BUTTON_TEXT_SINGLE_TITLE,
        chipBtnTextKey: CHIP_BUTTON_TEXT_TITLE,
    },
    [multiIncludeExcludeType.TOPIC]: {
        headerTextKey: HEADER_TEXT_KEY_TOPIC,
        emptyStateBtnTextKey: EMPTY_STATE_BUTTON_TOPIC,
        chipBtnTextSingleKey: CHIP_BUTTON_TEXT_SINGLE_TOPIC,
        chipBtnTextKey: CHIP_BUTTON_TEXT_TOPIC,
    },
};
